import {
    Box, Button, Divider, Typography, CircularProgress,
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Pagination,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "completed", label: "Completed" },
    { value: "under_review", label: "Under Review" },
    { value: "rejected", label: "Rejected" }
];

const ShowroomPayouts = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusInput, setStatusInput] = useState('pending');
    const [status, setStatus] = useState("pending");
    const [error, setError] = useState("");
    const [payouts, setPayouts] = useState([]);

    const getShowroomPayouts = async ({ search, page, limit, status } = {}) => {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (page) params.append('page', page);
        if (status) params.append('status', status);
        if (limit) params.append('limit', limit);
        try {
            setLoading(true)
            const response = await apiClient.get(`/showroompayout/getallpayouts`, { params });
            // console.log("Showroom Payouts Response:", response.data.data);
            if (response?.data?.status === true) {
                setPayouts(response?.data?.data);
                setTotalPages(response?.data?.totalPages || 1);
                setTotalItems(response?.data?.totalItems || 0);
            } else {
                setPayouts([]);
            }
        } catch (error) {
            setError(error?.response?.data?.message || "Something went wrong, try again.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        getShowroomPayouts({ search, page, limit, status });
    }, [search, page, limit, status]);

    return (
        <>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={2}
                    sx={{ mb: 2 }}
                >
                    <Typography variant="h3" gutterBottom sx={{ mb: { xs: 1, sm: 2 } }}>
                        Service Center Payouts
                    </Typography>
                </Stack>

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "stretch", sm: "center" }}
                    spacing={2}
                    sx={{ mb: 2 }}
                >
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "stretch", sm: "center" }}
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                        <TextField
                            size="small"
                            label="Search"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            sx={{ minWidth: { xs: "100%", sm: 200 } }}
                            fullWidth={true}
                        />
                        <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 150 } }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={statusInput}
                                onChange={e => setStatusInput(e.target.value)}
                            >
                                {STATUS_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ width: { xs: "100%", sm: "auto" } }}
                            onClick={() => {
                                setSearch(searchInput);
                                setStatus(statusInput);
                            }}
                        >
                            Search
                        </Button>
                    </Stack>
                    {/* Right side: Rows Per Page */}
                    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 120 }, mt: { xs: 2, sm: 0 } }}>
                        <InputLabel>Rows</InputLabel>
                        <Select
                            label="Rows"
                            value={limit}
                            onChange={e => setLimit(Number(e.target.value))}
                        >
                            {[5, 10, 20].map(num => (
                                <MenuItem key={num} value={num}>{num}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Stack>
                <Divider />
                <Box>
                    {loading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" height="100px">
                            <CircularProgress />
                        </Box>
                    ) : (
                        <TableContainer component={Paper} elevation={1}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Name</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Phone</strong></TableCell>
                                        <TableCell><strong>Bank Name</strong></TableCell>
                                        <TableCell><strong>Account Number</strong></TableCell>
                                        <TableCell><strong>IFSC Code</strong></TableCell>
                                        <TableCell><strong>Total Amount</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payouts.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} align="center">
                                                No showrooms found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        payouts.map((details) => (
                                            <TableRow key={details.id}>
                                                <TableCell>{details.showRoom?.name}</TableCell>
                                                <TableCell>{details.showRoom?.email}</TableCell>
                                                <TableCell>{details.showRoom?.contact}</TableCell>
                                                <TableCell>{details.bankAccount?.bankName}</TableCell>
                                                <TableCell>{details.bankAccount?.accountNumber}</TableCell>
                                                <TableCell>{details.bankAccount?.ifscCode}</TableCell>
                                                <TableCell>{details.totalAmount}</TableCell>
                                                <TableCell>{
                                                    STATUS_OPTIONS.find(opt => opt.value === details.status)?.label || details.status
                                                }</TableCell>
                                                <TableCell>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => navigate(`/showroom/update-payouts/${details.id}`, {
                                                            state: {
                                                                showroomName: details.showRoom?.name,
                                                                status: details.status
                                                            }
                                                        })}
                                                    >
                                                        Update
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    <Stack direction="row" justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(e, value) => setPage(value)}
                            color="primary"
                            shape="rounded"
                            showFirstButton
                            showLastButton
                        />
                    </Stack>
                    {error && (
                        <Box sx={{ mt: 2, color: 'red', }}>
                            <Typography variant="body2" color='error' align='center'>{error}</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </>
    )
}

export default ShowroomPayouts;
